// src/hooks/use-smart-vibes.js
import { useEffect, useRef, useState } from 'react';

// Use a stable ID per deploy so the worker URL changes and bypasses CDN cache.
const BUILD_ID =
  process.env.REACT_APP_COMMIT_SHA ||      // set in Vercel as ${VERCEL_GIT_COMMIT_SHA}
  process.env.REACT_APP_BUILD_ID ||        // optional custom fallback
  Date.now().toString();                   // dev fallback

// v8 worker
const WORKER_URL = `/smartVibes.worker.v8.js?v=${BUILD_ID}`;

// --- fast face scan (main thread). We only use this to provide hints to the worker. ---
let __fdInstance = null;
async function ensureFaceDetector() {
  if ('FaceDetector' in window) {
    if (!__fdInstance) __fdInstance = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 10 });
    return { type: 'browser', inst: __fdInstance };
  }
  // Lazy load MediaPipe only if browser FaceDetector is missing.
  if (!window.__mpFD) {
    await new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/face_detection.js';
      s.onload = res; s.onerror = () => rej(new Error('mediapipe load failed'));
      document.head.appendChild(s);
    });
    // eslint-disable-next-line no-undef
    window.__mpFD = new FaceDetection({ locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${f}` });
    window.__mpFD.setOptions({ model: 'short', minDetectionConfidence: 0.5 });
  }
  return { type: 'mp', inst: window.__mpFD };
}

async function quickFaceScan(items, { minCoverage = 0.03, resize = 512 } = {}) {
  // This is optional; if anything fails we just return an empty hints map.
  try {
    const { type, inst } = await ensureFaceDetector();
    const hints = {}; // url -> { faceCount, faceCoverage, pass }

    for (const it of items) {
      if (!it?.url) continue;
      const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = it.url; });
      const scale = Math.min(1, resize / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      const cx = c.getContext('2d'); cx.drawImage(img, 0, 0, c.width, c.height);

      let faces = [];
      if (type === 'browser') {
        const bitmap = await createImageBitmap(c);
        try {
          const dets = await inst.detect(bitmap);
          faces = (dets || []).map(f => f.boundingBox);
        } catch {
          faces = [];
        }
      } else {
        const res = await new Promise((resolve) => { inst.onResults(resolve); inst.send({ image: c }); });
        const dets = (res && res.detections) || [];
        const w = c.width, h = c.height;
        faces = dets.map(d => {
          const bb = d.boundingBox || (d.locationData && d.locationData.relativeBoundingBox) || {};
          let cx2 = Number(bb.xCenter || 0), cy2 = Number(bb.yCenter || 0),
              bw = Number(bb.width || 0),     bh = Number(bb.height || 0);
          const normalized = cx2 <= 1 && cy2 <= 1 && bw <= 1 && bh <= 1 && (bw > 0 || bh > 0);
          if (normalized) { cx2 *= w; cy2 *= h; bw *= w; bh *= h; }
          return { x: cx2 - bw/2, y: cy2 - bh/2, width: bw, height: bh };
        });
      }

      const area = c.width * c.height;
      let cover = 0;
      for (const b of faces) cover += Math.max(0, Math.min(b.width, c.width) * Math.min(b.height, c.height));
      const faceCoverage = area > 0 ? cover / area : 0;

      hints[it.url] = { faceCount: faces.length, faceCoverage, pass: faces.length > 0 && faceCoverage >= minCoverage };
    }
    return hints;
  } catch {
    return {};
  }
}

export function useSmartVibes() {
  const workerRef = useRef(null);

  const [status, setStatus] = useState('Idle');
  const [isReady, setIsReady] = useState(false);
  const [vibesAvailable, setVibesAvailable] = useState(true); // NEW
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);

  const [modelStatuses, setModelStatuses] = useState({
    worker: false,
    clip: false,
    yolo: false
  });

  // throttle progress to ~8 FPS
  const rafRef = useRef(0);
  const latestPctRef = useRef(0);
  const scheduleProgress = (pct, statusText) => {
    latestPctRef.current = pct;
    if (statusText) setStatus(statusText);
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      setProgress(Math.max(0, Math.min(100, Math.round(latestPctRef.current * 100))));
    });
  };

  useEffect(() => {
    const w = new Worker(WORKER_URL);
    workerRef.current = w;

    w.onerror = (e) => {
   console.error('Worker transport error:', e?.message || e);
   setStatus(`Error: ${e?.message || 'Worker error'}`);
   setIsAnalyzing(false);
   setVibesAvailable(false);   // NEW: don't block the flow
   setIsReady(true);           // NEW: allow Generate to proceed
 };

    w.onmessage = (ev) => {
      const { type, data, error, progress: prog } = ev.data || {};

       if (type === 'model-load-failed') {
   setVibesAvailable(false);   // models didn’t load → mark unavailable
   setIsReady(true);           // but don’t block the UI
   return;
 }


      if (type === 'error' || error) {
        const msg = (data && (data.error || data.message)) || error || 'Worker error';
        console.error('Worker error:', msg);
        setStatus(`Error: ${msg}`);
        setIsAnalyzing(false);
        return;
      }

      if (type === 'debug') {
        if (typeof data === 'string') {
          // keep old ready strings AND new ones
          if (data.includes('SCRIPT LOADED')) setModelStatuses((s) => ({ ...s, worker: true }));
          const low = data.toLowerCase();
          if ((low.includes('clip') && (low.includes('ready') || low.includes('session ok')))) {
            setModelStatuses((s) => ({ ...s, clip: true }));
          }
          if ((low.includes('yolo') && (low.includes('ready') || low.includes('session ok')))) {
            setModelStatuses((s) => ({ ...s, yolo: true }));
          }
        }
        return;
      }

      if (type === 'ready') {
        setIsReady(true);
        setStatus('Local AI models ready');
        setModelStatuses((s) => ({ ...s, worker: true }));
        setVibesAvailable(true);
        return;
      }

      if (type === 'progress') {
        scheduleProgress(prog || 0, data?.status);
        return;
      }

      if (type === 'analysis_complete') {
        const photos = (data && data.photos) || [];
        setResults(photos);      // set once, at the end
        setStatus('Analysis complete');
        setIsAnalyzing(false);
        setProgress(100);
        return;
      }
    };

    w.postMessage({ type: 'init' });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      w.terminate();
      workerRef.current = null;
    };
  }, []);

  /**
   * Analyze photos with optional options:
   *   { peopleDetector: 'tiered' | 'face-only', minFaceCoverage: number }
   * Defaults to 'tiered' to preserve existing behavior (no regression).
   */
  const analyzePhotos = async (photos, opts = {}) => {
    if (!workerRef.current || !Array.isArray(photos) || !photos.length) return;

    const config = {
      peopleDetector: opts.peopleDetector || 'tiered',   // default preserves current behavior
      minFaceCoverage: (typeof opts.minFaceCoverage === 'number' ? opts.minFaceCoverage : 0.03)
    };

    // Optional: quick face hints to help the worker skip YOLO when possible
    let hints = {};
    try {
      hints = await quickFaceScan(photos, { minCoverage: config.minFaceCoverage, resize: 512 });
    } catch {}

    setResults([]);
    setProgress(0);
    setStatus('Analyzing…');
    setIsAnalyzing(true);
    workerRef.current.postMessage({ type: 'analyze_photos', photos, hints, config });
  };

  return {
    status,
    isReady,
    vibesAvailable,   // NEW
    isAnalyzing,
    progress,
    results,
    modelStatuses,
    analyzePhotos,
  };
}
